import { z } from 'zod';
import { audienceSpecSchema } from '../audience/index';

/**
 * Contracten van het noodkanaal (Stap 35).
 *
 * Dit is de enige functie in het platform die **bewust door alle andere regels heen breekt**:
 * voorkeuren, stille uren, batches. Dat is geen uitzondering die we maken omdat het handig is,
 * maar de hele reden dat deze module bestaat — "ontruim hal 3" is geen mededeling die kan wachten
 * tot morgenochtend.
 *
 * Twee dingen zijn daarom anders dan overal elders:
 *
 * 1. **Namen zijn zichtbaar voor wie het incident leidt** (ADR-0060). Bij elke andere functie geldt
 *    ADR-0014 — aantallen, geen namen. Hier is "wie heeft nog niet geantwoord" de enige vraag die
 *    telt, en een aantal zonder namen beantwoordt die niet.
 * 2. **Een oefening is overal apart te zien.** In de data, op het scherm en in het rapport. Een
 *    oefening die eruitziet als een echt incident, leert mensen een echt incident te negeren.
 */

/** `safe` of `help`. Twee knoppen en niet meer: op een telefoon in paniek is drie er al één te veel. */
export const emergencyResponseStatusSchema = z.enum(['safe', 'help']);
export type EmergencyResponseStatus = z.infer<typeof emergencyResponseStatusSchema>;

export const incidentStatusSchema = z.enum(['active', 'closed']);
export type IncidentStatus = z.infer<typeof incidentStatusSchema>;

/**
 * Een sjabloon: de tekst die iemand onder druk niet meer hoeft te bedenken.
 *
 * De hele reden dat sjablonen bestaan. Wie om 03:00 een ontruiming moet starten, typt geen
 * instructie — hij kiest er een en drukt twee keer.
 */
export const emergencyTemplateSchema = z.object({
  id: z.guid(),
  title: z.string(),
  instruction: z.string(),
  createdAt: z.string().datetime(),
});
export type EmergencyTemplate = z.infer<typeof emergencyTemplateSchema>;

export const emergencyTemplateListSchema = z.object({
  items: z.array(emergencyTemplateSchema),
});
export type EmergencyTemplateList = z.infer<typeof emergencyTemplateListSchema>;

export const createEmergencyTemplateRequestSchema = z.object({
  title: z.string().trim().min(1, 'Geef het sjabloon een titel.').max(120),
  instruction: z
    .string()
    .trim()
    .min(1, 'Zeg wat mensen moeten doen.')
    .max(500, 'Houd het kort: dit staat op een telefoonscherm in een noodsituatie.'),
});
export type CreateEmergencyTemplateRequest = z.infer<typeof createEmergencyTemplateRequestSchema>;

export const startIncidentRequestSchema = z.object({
  /** Een sjabloon, of een eigen tekst. Eén van de twee. */
  templateId: z.guid().nullable().default(null),
  title: z.string().trim().max(120).optional(),
  instruction: z.string().trim().max(500).optional(),
  audience: audienceSpecSchema,
  /**
   * De vestiging waar dit speelt (Stap 29).
   *
   * Leeg betekent: het hele bedrijf, en dat blijft de standaard. Een bedrijfsbreed incident
   * sluit een incident per vestiging uit - een ontruiming van alles overstemt die van één
   * fabriek - maar twee vestigingen kunnen wél tegelijk ontruimen.
   */
  siteId: z.guid().nullable().default(null),
  /**
   * Een oefening.
   *
   * Geen standaardwaarde in dit schema: wie een noodmelding start, moet expliciet zeggen of het
   * een oefening is. Een vergeten vinkje dat "echt" betekent, is de verkeerde kant van die fout —
   * en een vergeten vinkje dat "oefening" betekent nog erger.
   */
  isDrill: z.boolean(),
  /**
   * De dubbele bevestiging, als woord.
   *
   * Niet een tweede knop maar een getypt woord: een dialoog met "weet je het zeker" wordt
   * weggeklikt zonder gelezen te worden. Dit moet letterlijk `START` zijn (of `OEFENING` bij een
   * oefening), en de server weigert alles anders.
   */
  confirmation: z.string(),
});
export type StartIncidentRequest = z.infer<typeof startIncidentRequestSchema>;

export const respondToIncidentRequestSchema = z.object({
  status: emergencyResponseStatusSchema,
  /** Optioneel, en kort: "ben bij de poort met Jan". Niemand typt een verhaal in paniek. */
  note: z.string().trim().max(200).default(''),
});
export type RespondToIncidentRequest = z.infer<typeof respondToIncidentRequestSchema>;

/**
 * Eén antwoord, **met naam**.
 *
 * De uitzondering op ADR-0014, en ze staat in ADR-0060. Bij een ontruiming is "wie staat er nog
 * binnen" de enige vraag, en die is met een aantal niet te beantwoorden. Alleen voor wie
 * `emergency.incident.view` heeft — en dat recht zit in geen enkele medewerkersrol.
 */
export const incidentResponseSchema = z.object({
  personId: z.guid(),
  displayName: z.string(),
  employeeNumber: z.string().nullable(),
  status: emergencyResponseStatusSchema,
  note: z.string(),
  at: z.string().datetime(),
});
export type IncidentResponse = z.infer<typeof incidentResponseSchema>;

/** Iemand die nog niets liet weten. De belangrijkste lijst op het scherm. */
export const incidentSilentPersonSchema = z.object({
  personId: z.guid(),
  displayName: z.string(),
  employeeNumber: z.string().nullable(),
  /** Hoeveel herinneringen zijn toestel al gekregen heeft. */
  reminders: z.number().int(),
});
export type IncidentSilentPerson = z.infer<typeof incidentSilentPersonSchema>;

export const incidentSchema = z.object({
  id: z.guid(),
  title: z.string(),
  instruction: z.string(),
  /** De vestiging waar dit speelt, of `null` voor het hele bedrijf (Stap 29). */
  siteId: z.guid().nullable().default(null),
  status: incidentStatusSchema,
  isDrill: z.boolean(),
  audienceDescription: z.string(),
  startedAt: z.string().datetime(),
  startedByName: z.string(),
  closedAt: z.string().datetime().nullable(),
  closedByName: z.string().nullable(),
  recipientCount: z.number().int(),
  safeCount: z.number().int(),
  helpCount: z.number().int(),
  silentCount: z.number().int(),
});
export type Incident = z.infer<typeof incidentSchema>;

export const incidentListSchema = z.object({ items: z.array(incidentSchema) });
export type IncidentList = z.infer<typeof incidentListSchema>;

/**
 * Het live overzicht.
 *
 * Wie hulp nodig heeft **eerst**, dan wie stil is, dan wie veilig is. Dat is de volgorde waarin
 * iemand die een ontruiming leidt ernaar kijkt, en niet de volgorde waarin de antwoorden
 * binnenkwamen.
 */
export const incidentBoardSchema = z.object({
  incident: incidentSchema,
  help: z.array(incidentResponseSchema),
  silent: z.array(incidentSilentPersonSchema),
  safe: z.array(incidentResponseSchema),
});
export type IncidentBoard = z.infer<typeof incidentBoardSchema>;

/**
 * Het incident zoals de medewerker het ziet.
 *
 * `null` betekent: er loopt niets voor mij. Dat is het antwoord dat de app het vaakst krijgt, en
 * daarom is het een leeg antwoord en geen `404`.
 */
export const myIncidentSchema = z.object({
  id: z.guid(),
  title: z.string(),
  instruction: z.string(),
  isDrill: z.boolean(),
  startedAt: z.string().datetime(),
  /** Mijn eigen antwoord, of leeg als ik nog niets liet weten. */
  myStatus: emergencyResponseStatusSchema.nullable(),
});
export type MyIncident = z.infer<typeof myIncidentSchema>;

export const myIncidentListSchema = z.object({ items: z.array(myIncidentSchema) });
export type MyIncidentList = z.infer<typeof myIncidentListSchema>;

/** Hoe vaak een toestel hoogstens een herinnering krijgt, en met welke tussentijd. */
export const EMERGENCY_MAX_REMINDERS = 5;
export const EMERGENCY_REMINDER_INTERVAL_MINUTES = 2;

/** Het woord dat een opsteller moet typen om te starten. */
export const EMERGENCY_CONFIRMATION = 'START';
export const EMERGENCY_DRILL_CONFIRMATION = 'OEFENING';
